import { MoneyError, add, money, subtract, zero } from './money.js';
import { apportionByValue } from './order/apportion.js';
import { can } from './permissions.js';
import { canTransition } from './state/transition.js';
/**
 * Purchase orders, goods receipt, three-way match and landed cost.
 *
 * §6.10 `INV-006`, §6.29 `SUP-001` to `SUP-005`, §6.28 `IMP-001` to `IMP-006`, Appendix C.5 and W.5,
 * Appendix D `MR-10`, release gate 8.
 *
 * **Where the spec is blank this module takes a parameter and has no default.** Over-receipt
 * tolerance, invoice quantity tolerance, price tolerance, the allocation basis, the fallback basis,
 * the channel preference order and the `soldQuantity` definition are all unspecified in the PRD. A
 * default would be a number chosen by nobody that the shop then lives under, so each is required
 * and a caller who wants "no tolerance" must say so with an explicit zero.
 *
 * **Rounding.** Nothing here rounds money. Landed cost is apportioned by the largest remainder
 * method, which hands every minor unit out and none twice, so it is a distribution and not one of
 * the three `MR-04` rounding points (steps 8, 11, 12). The only division is a whole-unit cap on
 * a percentage quantity tolerance, which is a count of pieces and not an amount of money. A
 * per-unit landed cost is deliberately not computed: dividing a line total by its quantity would
 * round, and no `MR-04` point owns that.
 */
export class PurchasingError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'PurchasingError';
    }
}
function assertWholeQuantity(value, what, allowZero) {
    if (!Number.isSafeInteger(value) || value < 0 || (!allowZero && value === 0)) {
        throw new PurchasingError('INVALID_QUANTITY', `${what} must be a whole ${allowZero ? 'non-negative' : 'positive'} number of pieces, got ${String(value)}.`);
    }
}
function assertSameCurrency(a, currency, what) {
    if (a.currency !== currency) {
        throw new MoneyError(`${what} is in ${a.currency} but the document is in ${currency}`);
    }
}
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const PERIOD = /^\d{4}-(0[1-9]|1[0-2])$/;
// ---------------------------------------------------------------------------------------------
// Purchase order state machine. Appendix C.5, W.5.
// ---------------------------------------------------------------------------------------------
export const PURCHASE_ORDER_STATES = [
    'draft',
    'sent',
    'acknowledged',
    'partially_received',
    'received',
    'invoice_matched',
    'closed',
    'cancelled',
    'disputed',
    'resolved',
];
/**
 * C.5 prints the happy path and three side edges. Two edges are read into it and flagged in the
 * report: `acknowledged → received`, because a single complete delivery would otherwise have to
 * pass through a `partially_received` that never happened, and `resolved → invoice_matched`, because
 * W.5 stops at `resolved` and a resolved dispute that cannot close would strand the order.
 */
export const PURCHASE_ORDER_TRANSITIONS = {
    draft: ['sent'],
    sent: ['acknowledged', 'cancelled'],
    acknowledged: ['partially_received', 'received'],
    partially_received: ['received'],
    received: ['invoice_matched', 'disputed'],
    invoice_matched: ['closed'],
    closed: [],
    cancelled: [],
    disputed: ['resolved'],
    resolved: ['invoice_matched'],
};
/**
 * W.5: "`disputed → resolved` requires manager". Checked against the Appendix B grid rather than a
 * literal role list, so the day the matrix moves, this moves with it.
 */
export function transitionPurchaseOrder(from, to, actor) {
    if (!canTransition(PURCHASE_ORDER_TRANSITIONS, from, to)) {
        throw new PurchasingError('INVALID_TRANSITION', `A purchase order cannot go from ${from} to ${to} (Appendix C.5).`);
    }
    if (from === 'disputed' && to === 'resolved') {
        if (actor === undefined || !can(actor.role, 'purchase_order_approve')) {
            throw new PurchasingError('MANAGER_REQUIRED', 'Resolving a disputed purchase order requires a manager (W.5).');
        }
    }
    return to;
}
/**
 * Validate an order's lines and return the one currency they are in.
 *
 * Shared by every function that takes `orderLines`, not only by the one that raises the order. The
 * lines arrive from the database on a later request, where `PurchaseOrderLine` is a shape the
 * persistence boundary asserted rather than one this module built, and a quantity or a sign that is
 * refused at `SUP-001` must not be accepted at receipt or at match time instead.
 */
function assertOrderLines(lines) {
    const first = lines[0];
    if (first === undefined) {
        throw new PurchasingError('EMPTY_ORDER', 'A purchase order needs at least one line (SUP-001).');
    }
    const currency = first.unitCost.currency;
    const seen = new Set();
    for (const line of lines) {
        if (seen.has(line.lineId)) {
            throw new PurchasingError('DUPLICATE_LINE', `Line ${line.lineId} appears twice.`);
        }
        seen.add(line.lineId);
        assertWholeQuantity(line.orderedQuantity, `Ordered quantity on ${line.lineId}`, false);
        assertSameCurrency(line.unitCost, currency, `Unit cost on ${line.lineId}`);
        if (line.unitCost.minor < 0n) {
            throw new PurchasingError('NEGATIVE_COST', `Unit cost on ${line.lineId} is negative.`);
        }
    }
    return currency;
}
/**
 * How much of a line has been received to date, refusing a count that is not a whole number of
 * pieces. The map is read back from storage, so a fractional or negative value has to be caught
 * here: left alone it reaches `BigInt()` as a raw `RangeError`, or settles into a cumulative
 * received quantity of three and a half frames.
 */
function receivedFor(receivedToDate, lineId) {
    const received = receivedToDate.get(lineId) ?? 0;
    assertWholeQuantity(received, `Received to date on ${lineId}`, true);
    return received;
}
/** The order total, after refusing a line set that could not be matched later. `SUP-001`. */
export function purchaseOrderTotal(lines) {
    const currency = assertOrderLines(lines);
    let total = zero(currency);
    for (const line of lines) {
        total = add(total, money(line.unitCost.minor * BigInt(line.orderedQuantity), currency));
    }
    return total;
}
/**
 * Build an order line from a supplier price list entry. Lead time travels with the line so
 * `SUP-005`'s expected delivery can be derived, but turning days into a date needs the branch
 * calendar and is the caller's. Suggested quantities are `INV-011` and are not built here.
 */
export function lineFromPriceList(lineId, entry, orderedQuantity) {
    assertWholeQuantity(orderedQuantity, `Ordered quantity on ${lineId}`, false);
    assertWholeQuantity(entry.leadTimeDays, `Lead time for ${entry.sku}`, true);
    return {
        lineId,
        sku: entry.sku,
        orderedQuantity,
        unitCost: entry.unitCost,
        leadTimeDays: entry.leadTimeDays,
    };
}
export const SUPPLIER_CHANNELS = ['api', 'portal', 'email_structured', 'whatsapp_image'];
/**
 * `SUP-003`: "by whatever the supplier supports". Which suppliers support which channel, and which
 * the shop would rather use, are both unspecified, so both are arguments. Retry and timeout policy
 * for a failed send is also unspecified and is not modelled here.
 */
export function chooseTransmissionChannel(supported, preferenceOrder) {
    const found = preferenceOrder.find((c) => supported.includes(c));
    if (found === undefined) {
        throw new PurchasingError('NO_CHANNEL', 'The supplier supports none of the channels in the preference order, so the order cannot be sent (SUP-003).');
    }
    return found;
}
const AWAITING_GOODS = ['sent', 'acknowledged', 'partially_received'];
/**
 * `SUP-005`. The supplier-side status vocabulary and polling cadence are unspecified, so this takes
 * the state the shop already holds and does not try to normalise a supplier's own words.
 */
export function orderStatusView(input) {
    if (!ISO_DATE.test(input.asOf)) {
        throw new PurchasingError('INVALID_DATE', `asOf must be YYYY-MM-DD, got ${input.asOf}.`);
    }
    if (input.expectedDeliveryOn !== undefined && !ISO_DATE.test(input.expectedDeliveryOn)) {
        throw new PurchasingError('INVALID_DATE', 'expectedDeliveryOn must be YYYY-MM-DD.');
    }
    assertWholeQuantity(input.backorderQuantity, 'Back-order quantity', true);
    const overdue = input.expectedDeliveryOn !== undefined &&
        AWAITING_GOODS.includes(input.state) &&
        input.asOf > input.expectedDeliveryOn;
    return {
        state: input.state,
        ...(input.expectedDeliveryOn === undefined
            ? {}
            : { expectedDeliveryOn: input.expectedDeliveryOn }),
        overdue,
        backorderQuantity: input.backorderQuantity,
    };
}
function assertQuantityTolerance(t, what) {
    const n = t.kind === 'absolute' ? t.units : t.basisPoints;
    assertWholeQuantity(n, `${what} tolerance`, true);
}
/**
 * The currency is checked here rather than where the two prices are compared, because a comparison
 * only happens when a price actually differs: a tolerance in the wrong currency would otherwise be
 * accepted on every invoice that happened to agree, and refused on the first one that did not.
 */
function assertPriceTolerance(t, currency) {
    if (t.kind === 'absolute') {
        if (t.perUnit.minor < 0n) {
            throw new PurchasingError('INVALID_TOLERANCE', 'A price tolerance cannot be negative.');
        }
        assertSameCurrency(t.perUnit, currency, 'Price tolerance');
    }
    else {
        assertWholeQuantity(t.basisPoints, 'Price tolerance', true);
    }
}
/**
 * The most pieces acceptable against `base`. A percentage of a piece count floors to whole pieces:
 * half a frame cannot arrive, and flooring is the cautious side of "within tolerance".
 */
export function maxQuantityWithin(base, tolerance) {
    assertWholeQuantity(base, 'The quantity a tolerance is measured against', true);
    assertQuantityTolerance(tolerance, 'Quantity');
    if (tolerance.kind === 'absolute')
        return base + tolerance.units;
    return base + Number((BigInt(base) * BigInt(tolerance.basisPoints)) / 10000n);
}
/**
 * Record one goods receipt against an order, cumulatively.
 *
 * Over-receipt within tolerance is accepted with a flag (§15.6 step 3). Beyond it, the excess is
 * reported as `excessQuantity` instead of throwing: the cartons are on the counter whether or not
 * the system approves, and refusing the whole receipt would leave the accepted part unrecorded.
 *
 * `received` means receiving is finished: every line filled, or the caller says the supplier will
 * ship no more (`supplierWillShipNoMore`). The second is how a short shipment is closed, and it is
 * the caller's statement because nothing in the spec says what ends a back-order.
 *
 * **A receipt has to receive something.** An empty line set is only accepted as the explicit close
 * of a back-order, and an order on which nothing has ever arrived cannot be closed as `received` at
 * all: that is an order to cancel, not goods to book. Without both guards a `sent` order with no
 * delivery reaches `received`, and the three-way match then pays an invoice against a claim for the
 * entire order.
 */
export function recordReceipt(input) {
    if (input.state !== 'acknowledged' && input.state !== 'partially_received') {
        throw new PurchasingError('RECEIPT_NOT_ALLOWED', `Goods cannot be received against an order in ${input.state} (C.5).`);
    }
    assertQuantityTolerance(input.overReceiptTolerance, 'Over-receipt');
    assertOrderLines(input.orderLines);
    if (input.receipt.length === 0 && input.supplierWillShipNoMore !== true) {
        throw new PurchasingError('EMPTY_RECEIPT', 'A goods receipt must record at least one line. An empty one is only the close of a back-order, which needs supplierWillShipNoMore.');
    }
    const known = new Set(input.orderLines.map((l) => l.lineId));
    const incoming = new Map();
    for (const r of input.receipt) {
        if (!known.has(r.lineId)) {
            throw new PurchasingError('UNKNOWN_LINE', `Receipt line ${r.lineId} is not on the order.`);
        }
        if (incoming.has(r.lineId)) {
            throw new PurchasingError('DUPLICATE_LINE', `Receipt line ${r.lineId} appears twice.`);
        }
        assertWholeQuantity(r.receivedQuantity, `Received quantity on ${r.lineId}`, false);
        incoming.set(r.lineId, r.receivedQuantity);
    }
    const lines = input.orderLines.map((line) => {
        const previous = receivedFor(input.receivedToDate, line.lineId);
        const arrived = incoming.get(line.lineId) ?? 0;
        const room = Math.max(0, maxQuantityWithin(line.orderedQuantity, input.overReceiptTolerance) - previous);
        const accepted = Math.min(arrived, room);
        const excess = arrived - accepted;
        const cumulative = previous + accepted;
        return {
            lineId: line.lineId,
            orderedQuantity: line.orderedQuantity,
            previouslyReceived: previous,
            acceptedQuantity: accepted,
            excessQuantity: excess,
            cumulativeReceived: cumulative,
            backorderQuantity: Math.max(0, line.orderedQuantity - cumulative),
            flag: excess > 0
                ? 'over_receipt_beyond_tolerance'
                : cumulative > line.orderedQuantity
                    ? 'over_receipt_within_tolerance'
                    : 'none',
        };
    });
    if (!lines.some((l) => l.cumulativeReceived > 0)) {
        throw new PurchasingError('NOTHING_RECEIVED', 'No goods have arrived against this order, so there is nothing to receive and nothing to close. C.5 cancels only from sent, so an acknowledged order the supplier never ships has no legal next state at all: a spec gap, not a state this can invent.');
    }
    const filled = lines.every((l) => l.cumulativeReceived >= l.orderedQuantity);
    return {
        lines,
        state: filled || input.supplierWillShipNoMore === true ? 'received' : 'partially_received',
    };
}
/**
 * `SUP-004`: pre-fill a goods receipt from an advance shipping notice. It only proposes; the person
 * at the dock still confirms what physically arrived. The ASN format and how it is tied to an order
 * are unspecified, so matching here is by the order line id the ingestion layer already resolved.
 */
export function prefillReceiptFromAsn(orderLines, asn) {
    const known = new Set(orderLines.map((l) => l.lineId));
    const receipt = [];
    const unmatched = [];
    const seen = new Set();
    for (const a of asn) {
        assertWholeQuantity(a.shippedQuantity, `Shipped quantity on ${a.poLineId}`, true);
        if (seen.has(a.poLineId)) {
            throw new PurchasingError('DUPLICATE_LINE', `ASN line ${a.poLineId} appears twice.`);
        }
        seen.add(a.poLineId);
        if (!known.has(a.poLineId)) {
            unmatched.push(a.poLineId);
        }
        else if (a.shippedQuantity > 0) {
            receipt.push({ lineId: a.poLineId, receivedQuantity: a.shippedQuantity });
        }
    }
    return {
        receipt,
        unmatchedAsnLines: unmatched,
        notShipped: orderLines
            .map((l) => l.lineId)
            .filter((id) => !receipt.some((r) => r.lineId === id)),
    };
}
function priceWithinTolerance(ordered, invoiced, tolerance) {
    const diff = invoiced.minor - ordered.minor;
    const abs = diff < 0n ? -diff : diff;
    if (tolerance.kind === 'absolute') {
        return abs <= tolerance.perUnit.minor;
    }
    // Cross-multiplied so no fraction of a minor unit is ever formed.
    return abs * 10000n <= ordered.minor * BigInt(tolerance.basisPoints);
}
/**
 * Match an order against its cumulative receipts and a supplier invoice. `INV-006`.
 *
 * "A mismatch opens a query, it does not post" (§15.6 step 4), so a blocking finding yields
 * `query` and the `disputed` transition, and nothing here writes a ledger entry.
 *
 * The order can only be disputed from `received` (W.5). While it is still `partially_received`
 * every verdict is `pending`, because an invoice that bills ahead of the goods is the normal case
 * for goods in transit and not yet a disagreement. Findings are still returned so the shop sees
 * them early. Price variance is measured both ways: a cheaper invoice means the agreed price on
 * the order was stale, which is as worth a human look as a dearer one.
 */
export function matchThreeWay(input) {
    if (input.state !== 'partially_received' && input.state !== 'received') {
        throw new PurchasingError('MATCH_NOT_ALLOWED', `An order in ${input.state} cannot be matched. Only partially_received or received orders can (C.5).`);
    }
    const { tolerances } = input;
    const currency = assertOrderLines(input.orderLines);
    assertQuantityTolerance(tolerances.overReceipt, 'Over-receipt');
    assertQuantityTolerance(tolerances.invoiceQuantity, 'Invoice quantity');
    assertPriceTolerance(tolerances.price, currency);
    const known = new Set(input.orderLines.map((l) => l.lineId));
    const invoiced = new Map();
    const findings = [];
    for (const inv of input.invoiceLines) {
        if (invoiced.has(inv.lineId)) {
            throw new PurchasingError('DUPLICATE_LINE', `Invoice line ${inv.lineId} appears twice.`);
        }
        assertWholeQuantity(inv.invoicedQuantity, `Invoiced quantity on ${inv.lineId}`, true);
        invoiced.set(inv.lineId, inv);
        if (!known.has(inv.lineId)) {
            findings.push({
                kind: 'unknown_invoice_line',
                lineId: inv.lineId,
                blocking: true,
                withinTolerance: false,
            });
        }
    }
    // An invoice that leaves a received line out bills nothing for it, which is the same fact as
    // billing it at zero, so the two are read the same way. With no invoice at all there is nothing
    // to compare the goods against and the quantity findings are not raised.
    const invoiceArrived = input.invoiceLines.length > 0;
    for (const line of input.orderLines) {
        const received = receivedFor(input.receivedToDate, line.lineId);
        const inv = invoiced.get(line.lineId);
        const invoicedQuantity = inv?.invoicedQuantity ?? 0;
        if (received < line.orderedQuantity && input.state === 'received') {
            const short = line.orderedQuantity - received;
            findings.push({
                kind: 'short_shipment',
                lineId: line.lineId,
                blocking: false,
                withinTolerance: false,
                quantity: short,
                amount: money(line.unitCost.minor * BigInt(short), line.unitCost.currency),
            });
        }
        if (received > line.orderedQuantity) {
            const within = received <= maxQuantityWithin(line.orderedQuantity, tolerances.overReceipt);
            findings.push({
                kind: 'over_shipment',
                lineId: line.lineId,
                blocking: !within,
                withinTolerance: within,
                quantity: received - line.orderedQuantity,
            });
        }
        if (invoiceArrived && invoicedQuantity > received) {
            const within = invoicedQuantity <= maxQuantityWithin(received, tolerances.invoiceQuantity);
            findings.push({
                kind: 'invoice_quantity_over',
                lineId: line.lineId,
                blocking: !within,
                withinTolerance: within,
                quantity: invoicedQuantity - received,
            });
        }
        else if (invoiceArrived && invoicedQuantity < received) {
            findings.push({
                kind: 'invoice_quantity_under',
                lineId: line.lineId,
                blocking: false,
                withinTolerance: true,
                quantity: received - invoicedQuantity,
            });
        }
        // No invoiced price to compare: an omitted line has no price, only a missing quantity.
        if (inv === undefined)
            continue;
        const diff = subtract(inv.unitPrice, line.unitCost);
        if (diff.minor !== 0n) {
            const within = priceWithinTolerance(line.unitCost, inv.unitPrice, tolerances.price);
            findings.push({
                kind: 'price_variance',
                lineId: line.lineId,
                blocking: !within,
                withinTolerance: within,
                amount: diff,
            });
        }
    }
    const blocking = findings.some((f) => f.blocking);
    const judgeable = input.state === 'received';
    const verdict = !judgeable || input.invoiceLines.length === 0 ? 'pending' : blocking ? 'query' : 'matched';
    return {
        verdict,
        findings,
        nextState: verdict === 'matched' ? 'invoice_matched' : verdict === 'query' ? 'disputed' : null,
    };
}
/**
 * Record a foreign-currency purchase. The booked base amount is supplied rather than derived:
 * converting needs rounding, no `MR-04` point owns it, and which rate is authoritative and when it
 * locks are unspecified (`IMP-004`), so the caller states what was actually booked.
 */
export function recordForeignPurchase(input) {
    if (input.foreign.currency === input.bookedBase.currency) {
        throw new PurchasingError('NOT_FOREIGN', 'A foreign purchase must differ in currency from the base amount.');
    }
    if (input.rate.numerator <= 0n || input.rate.denominator <= 0n) {
        throw new PurchasingError('INVALID_RATE', 'An exchange rate must be positive.');
    }
    if (!ISO_DATE.test(input.rateDate)) {
        throw new PurchasingError('INVALID_DATE', `rateDate must be YYYY-MM-DD, got ${input.rateDate}.`);
    }
    return input;
}
/**
 * `MR-10`: the realised difference on settlement, as its own figure. Positive means the shop paid
 * more base currency than it booked (a loss). It is never added to goods cost and never to landed
 * cost, which is why it is returned alone.
 */
export function realisedExchangeDifference(booked, settledBase) {
    return subtract(settledBase, booked.bookedBase);
}
// ---------------------------------------------------------------------------------------------
// Shipments and landed cost. IMP-001 to IMP-006, release gate 8.
// ---------------------------------------------------------------------------------------------
export const SHIPMENT_COST_KINDS = [
    'freight',
    'insurance',
    'customs_duty',
    'clearing_agent',
    'port_charges',
    'inland_transport',
    'bank_charges',
];
export const ALLOCATION_BASES = ['value', 'weight', 'volume', 'unit_count'];
export const SHIPMENT_TRACKING_STATES = [
    'ordered',
    'shipped',
    'in_transit',
    'at_customs',
    'cleared',
    'received',
];
/**
 * `IMP-006`. The requirement lists six states and no edges at all, so every edge here is read into
 * it. What triggers each step (ASN, carrier feed, a person) is unspecified, so only order is held.
 * `in_transit → cleared` is the one skip: a domestic or air-courier shipment never stops at
 * customs. It is an assumption, not the spec, as much as the two purchase order edges above.
 */
export const SHIPMENT_TRACKING_TRANSITIONS = {
    ordered: ['shipped'],
    shipped: ['in_transit'],
    in_transit: ['at_customs', 'cleared'],
    at_customs: ['cleared'],
    cleared: ['received'],
    received: [],
};
export function advanceShipment(from, to) {
    if (!canTransition(SHIPMENT_TRACKING_TRANSITIONS, from, to)) {
        throw new PurchasingError('INVALID_TRANSITION', `A shipment cannot go from ${from} to ${to} (IMP-006).`);
    }
    return to;
}
function basisValue(line, basis) {
    switch (basis) {
        case 'value':
            return line.goodsValue.minor;
        case 'weight':
            return line.weight;
        case 'volume':
            return line.volume;
        case 'unit_count':
            return BigInt(line.receivedQuantity);
    }
}
function validateShipmentLines(lines, currency) {
    const seen = new Set();
    for (const l of lines) {
        if (seen.has(l.lineId)) {
            throw new PurchasingError('DUPLICATE_LINE', `Shipment line ${l.lineId} appears twice.`);
        }
        seen.add(l.lineId);
        assertWholeQuantity(l.receivedQuantity, `Received quantity on ${l.lineId}`, false);
        assertWholeQuantity(l.soldQuantity, `Sold quantity on ${l.lineId}`, true);
        if (l.soldQuantity > l.receivedQuantity) {
            throw new PurchasingError('SOLD_EXCEEDS_RECEIVED', `Line ${l.lineId} cannot have sold more than it received.`);
        }
        assertSameCurrency(l.goodsValue, currency, `Goods value on ${l.lineId}`);
    }
}
/**
 * Apportion shipment costs across received lines. `IMP-001`, `IMP-002`, `IMP-005`.
 *
 * Each cost is distributed on its own by the largest remainder method, so every cost's shares sum
 * to it exactly and the per-line totals sum to the cost total with no unit lost or invented.
 *
 * **A missing basis is refused, not guessed.** A weight basis over lines with no catalogue weight,
 * or a basis whose total is zero, throws `BASIS_MISSING`. `IMP-002` does not say what to fall back
 * to, so `fallbackBasis` is an explicit argument and applies to a whole cost, never line by line:
 * shares computed on two different measures cannot be compared.
 */
export function allocateShipmentCosts(input) {
    const currency = input.lines[0]?.goodsValue.currency ?? input.costs[0]?.amount.currency;
    if (currency === undefined) {
        return { audit: [], details: [], perLine: [] };
    }
    validateShipmentLines(input.lines, currency);
    const totals = new Map(input.lines.map((l) => [l.lineId, 0n]));
    const audit = [];
    const details = [];
    input.costs.forEach((cost, costIndex) => {
        assertSameCurrency(cost.amount, currency, `${cost.kind} cost`);
        const eligible = cost.hsCode === undefined ? input.lines : input.lines.filter((l) => l.hsCode === cost.hsCode);
        const requested = cost.basis ?? input.defaultBasis;
        if (eligible.length === 0) {
            if (cost.amount.minor === 0n)
                return;
            throw new PurchasingError('NO_ELIGIBLE_LINES', `The ${cost.kind} cost has nowhere to go: no line carries HS code ${cost.hsCode ?? '(any)'}.`);
        }
        const attempt = (basis) => {
            const values = [];
            for (const l of eligible) {
                const v = basisValue(l, basis);
                if (v === undefined)
                    return undefined;
                if (v < 0n) {
                    throw new PurchasingError('NEGATIVE_BASIS', `Line ${l.lineId} has a negative ${basis}.`);
                }
                values.push(v);
            }
            return values.reduce((a, b) => a + b, 0n) === 0n ? undefined : values;
        };
        const free = cost.amount.minor === 0n;
        let basisUsed = requested;
        let values = free ? eligible.map(() => 0n) : attempt(requested);
        if (values === undefined &&
            input.fallbackBasis !== undefined &&
            input.fallbackBasis !== requested) {
            basisUsed = input.fallbackBasis;
            values = attempt(basisUsed);
        }
        if (values === undefined) {
            throw new PurchasingError('BASIS_MISSING', `Cannot allocate the ${cost.kind} cost by ${requested}: the data is missing or sums to zero, and no usable fallback basis was given (IMP-002).`);
        }
        const shares = free
            ? eligible.map((l) => ({ id: l.lineId, shareMinor: 0n }))
            : apportionByValue(cost.amount.minor, eligible.map((l, i) => ({ id: l.lineId, valueMinor: values[i] ?? 0n })));
        for (const s of shares) {
            totals.set(s.id, (totals.get(s.id) ?? 0n) + s.shareMinor);
            details.push({ costIndex, lineId: s.id, share: money(s.shareMinor, currency) });
        }
        audit.push({
            costIndex,
            kind: cost.kind,
            basisRequested: requested,
            basisUsed,
            usedFallback: basisUsed !== requested,
            basisTotal: values.reduce((a, b) => a + b, 0n),
            lineIds: eligible.map((l) => l.lineId),
        });
    });
    return {
        audit,
        details,
        perLine: input.lines.map((l) => ({
            lineId: l.lineId,
            share: money(totals.get(l.lineId) ?? 0n, currency),
        })),
    };
}
/**
 * Landed cost, expressible after the fact. Release gate 8, `IMP-003`.
 *
 * Freight and duty invoices arrive after goods are sold, and may be revised again later, so this
 * does not assume a first pass. Pass what each line already carries as `prior` and the whole
 * shipment's costs as they stand now: the result is the change per line, split between pieces sold
 * (COGS, in `currentPeriod`) and pieces unsold (inventory). Calling it again after a revised
 * invoice yields only the new difference, so no cost is counted twice and nothing is backdated.
 * No "finalised" event is required first: what ends a shipment's costs is unspecified.
 *
 * **The split is a difference of two exact splits, not a split of the difference.** `toCogs` is the
 * sold share of the new total less the sold share of the prior total, both apportioned by piece
 * count. `toCogs + toInventory` still equals `delta` exactly, and in addition, for an unchanged
 * sold count, the COGS posted over any number of revisions adds up to the sold share of the final
 * total. (Pieces that sell between revisions carry their cost to COGS through the sale posting,
 * which is not this function's to repeat.) Splitting each delta on its
 * own does not: a freight cost of 2 on a line with two of four pieces sold splits 1 and 1 in one
 * pass but sends the whole 2 to COGS when it arrives as 1 and then 1, and a cost revised from 200
 * down to 100 leaves 34 in COGS where the sold share of 100 is 33. The amounts are a minor unit
 * each, and they are the difference between a cost adjustment that reverses and one that drifts.
 */
export function reviseLandedCost(input) {
    if (!PERIOD.test(input.currentPeriod)) {
        throw new PurchasingError('INVALID_PERIOD', `currentPeriod must be YYYY-MM, got ${input.currentPeriod}.`);
    }
    const known = new Set(input.lines.map((l) => l.lineId));
    for (const id of input.prior.keys()) {
        if (!known.has(id)) {
            throw new PurchasingError('UNKNOWN_LINE', `Prior cost on ${id} has no line in this shipment to move it to.`);
        }
    }
    const allocation = allocateShipmentCosts(input);
    const currency = allocation.perLine[0]?.share.currency;
    if (currency === undefined) {
        throw new PurchasingError('EMPTY_SHIPMENT', 'A shipment with no lines has nothing to allocate to.');
    }
    const lineById = new Map(input.lines.map((l) => [l.lineId, l]));
    /** An amount split between the pieces of one line already sold and those still on the shelf. */
    const splitByPieces = (amountMinor, line) => {
        const shares = apportionByValue(amountMinor, [
            { id: 'sold', valueMinor: BigInt(line.soldQuantity) },
            { id: 'unsold', valueMinor: BigInt(line.receivedQuantity - line.soldQuantity) },
        ]);
        return {
            sold: shares.find((s) => s.id === 'sold')?.shareMinor ?? 0n,
            unsold: shares.find((s) => s.id === 'unsold')?.shareMinor ?? 0n,
        };
    };
    const adjustments = allocation.perLine.map(({ lineId, share }) => {
        const line = lineById.get(lineId);
        if (line === undefined)
            throw new PurchasingError('UNKNOWN_LINE', `Line ${lineId} vanished.`);
        const prior = input.prior.get(lineId) ?? zero(currency);
        assertSameCurrency(prior, currency, `Prior cost on ${lineId}`);
        const delta = subtract(share, prior);
        const now = splitByPieces(share.minor, line);
        const before = splitByPieces(prior.minor, line);
        return {
            lineId,
            priorShare: prior,
            newShare: share,
            delta,
            toCogs: money(now.sold - before.sold, currency),
            toInventory: money(now.unsold - before.unsold, currency),
            landedLineCost: add(line.goodsValue, share),
        };
    });
    return {
        allocation,
        adjustments,
        period: input.currentPeriod,
        totalDelta: adjustments.reduce((sum, a) => add(sum, a.delta), zero(currency)),
    };
}
//# sourceMappingURL=purchasing.js.map